import { useClearableReducer } from "@client-shared/hooks/use-clearable-reducer";
import i18n from "@client-shared/lib/i18n";
import { useSFDCAuthState } from "@client-shared/providers/auth/useSFDCAuthState";
import { useVendorContext } from "@client-shared/providers/vendor";
import * as api from "@client-shared/services/api";
import { handleDisplayErrorData } from "@client-shared/services/api/utilities/handleDisplayErrorData";
import type { UserProfileRequest } from "@shared/schemas";
import React from "react";
import { initialState, reducer, setError, setLoading, setProfile, setSaving } from "./actions";
import { UserProfileContextState, UseUserProfileUtils } from "./types";

export const UserProfileContext = React.createContext<UserProfileContextState>([initialState, () => {}, {} as UseUserProfileUtils]);

export const UserProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch, resetState] = useClearableReducer(reducer, initialState);
  const [{ currentVendor }] = useVendorContext();
  const [{ sfdcAuthToken }] = useSFDCAuthState();
  const isAuthReady = Boolean(sfdcAuthToken);

  const userId = currentVendor?.userId;
  const vendorKey = currentVendor?.vendorKey;

  const requestVendorKeyRef = React.useRef<string | undefined>(undefined);
  // Bumped on logout and vendor changes so a write that finishes afterwards doesn't refresh the new session's profile.
  const sessionGenerationRef = React.useRef(0);

  const clearState = React.useCallback(() => {
    sessionGenerationRef.current += 1;
    requestVendorKeyRef.current = undefined;
    resetState();
  }, [resetState]);

  const refreshProfile = React.useCallback(async () => {
    if (!userId || !vendorKey || !isAuthReady) {
      return;
    }

    const requestKey = vendorKey;
    requestVendorKeyRef.current = requestKey;
    dispatch(setLoading(true));
    dispatch(setError(undefined));
    try {
      const profile = await api.getUserProfile({ userId, vendorKey });
      if (requestVendorKeyRef.current !== requestKey) return;
      dispatch(setProfile(profile));
    } catch (e) {
      if (requestVendorKeyRef.current !== requestKey) return;
      handleDisplayErrorData(
        i18n.t("apiErrorMessages.getUserProfile.message"),
        i18n.t("apiErrorMessages.getUserProfile.title"),
        e as Error
      );
      dispatch(setError(i18n.t("apiErrorMessages.getUserProfile.message")));
    } finally {
      if (requestVendorKeyRef.current === requestKey) {
        dispatch(setLoading(false));
      }
    }
  }, [userId, vendorKey, isAuthReady, dispatch]);

  const updateProfile = React.useCallback(
    async (profile: UserProfileRequest): Promise<boolean> => {
      const generation = sessionGenerationRef.current;
      dispatch(setSaving(true));
      dispatch(setError(undefined));
      try {
        await api.updateUserProfile(profile);
        if (generation === sessionGenerationRef.current) {
          await refreshProfile();
        }
        return true;
      } catch (e) {
        handleDisplayErrorData(
          i18n.t("apiErrorMessages.updateUserProfile.message"),
          i18n.t("apiErrorMessages.updateUserProfile.title"),
          e as Error
        );
        dispatch(setError(i18n.t("apiErrorMessages.updateUserProfile.message")));
        return false;
      } finally {
        dispatch(setSaving(false));
      }
    },
    [refreshProfile, dispatch]
  );

  // Clear stale profile and invalidate any in-flight request marker when the
  // active vendor changes so stale responses cannot repopulate the previous
  // vendor's data after logout or vendor switching.
  React.useEffect(() => {
    sessionGenerationRef.current += 1;
    requestVendorKeyRef.current = undefined;
    dispatch(setProfile(undefined));
  }, [userId, vendorKey, dispatch]);

  React.useEffect(() => {
    if (!userId || !vendorKey || !isAuthReady) return;
    if (state.profile) return;
    void refreshProfile();
  }, [userId, vendorKey, isAuthReady, state.profile, refreshProfile]);

  const uploadLogo = React.useCallback(
    async (file: File): Promise<boolean> => {
      const generation = sessionGenerationRef.current;
      try {
        await api.uploadRoasterLogo(file);
        if (generation === sessionGenerationRef.current) {
          await refreshProfile();
        }
        return true;
      } catch {
        return false;
      }
    },
    [refreshProfile]
  );

  const utils: UseUserProfileUtils = React.useMemo(
    () => ({ refreshProfile, updateProfile, uploadLogo, clearState }),
    [refreshProfile, updateProfile, uploadLogo, clearState]
  );

  const contextValue: UserProfileContextState = React.useMemo(() => [state, dispatch, utils], [state, utils, dispatch]);

  return <UserProfileContext.Provider value={contextValue}>{children}</UserProfileContext.Provider>;
};
